import React, { memo } from "react";

import { Box } from "@UI/Components/Box/Box";
import { Image } from "@UI/Components/Image/Image";
import useThemeToken from "@UI/hooks/useThemeToken";

import { IServiceProviderPin } from "../../utils/serviceProviderPins";

import mapImage from "assets/images/task-creation/sp-map.jpg";

type Variant = "desktop" | "mobile";
type Side = "left" | "right";

export interface IServiceProviderMapProps {
  variant: Variant;
  /** Already sliced to this variant's pin count by the caller. */
  pins: IServiceProviderPin[];
  /** Which side of the form the desktop column sits on, so it fades towards
   * the form: the description screen's is on the left, the auth step's on the
   * right. Ignored by the mobile strip, which always sits above the form. */
  side?: Side;
}

/**
 * Left/top of each pin's 50x50 face, as % of the box — Figma exact. Ordered
 * the same as `getServiceProviderPins` (Alex, Karim, Lina, Anita), so
 * `pins[i]` always lands on `POSITIONS[variant][i]`.
 */
const POSITIONS: Record<Variant, { left: number; top: number }[]> = {
  desktop: [
    { left: 40.27, top: 18.27 },
    { left: 6.48, top: 73.8 },
    { left: 71.71, top: 51.88 },
    { left: 40.87, top: 83.98 },
  ],
  mobile: [
    { left: 5.87, top: 17.78 },
    { left: 46.13, top: 12.73 },
    { left: 72.13, top: 46.97 },
  ],
};

// Figma crops one shared map image differently per breakpoint (`objectFit:
// fill` over an absolutely-positioned, oversized image) rather than shipping
// two pre-cropped assets.
const MAP_CROP: Record<Variant, Record<string, string>> = {
  desktop: { width: "284.1%", height: "100%", left: "-146.8%", top: "0" },
  mobile: { width: "100%", height: "151.5%", left: "0", top: "-25.76%" },
};

// Where the fade-to-background gradient starts (transparent -> the page's own
// background) — the desktop column fades over its last third, the mobile strip
// over its whole self.
const FADE_STOP: Record<Variant, string> = {
  desktop: "71.2%",
  mobile: "0%",
};

// The mobile strip fades downwards, into the form below it; the desktop column
// sideways, into the form beside it.
const fadeAngle = (variant: Variant, side: Side): string => {
  if (variant === "mobile") return "180deg";

  return side === "left" ? "90deg" : "-90deg";
};

export const ServiceProviderMap = memo<IServiceProviderMapProps>(
  ({ variant, pins, side = "left" }) => {
    const [sand100] = useThemeToken("colors", "sand.100");
    const isDesktop = variant === "desktop";

    return (
      <Box
        position="relative"
        width={isDesktop ? "509px" : "100%"}
        height={isDesktop ? "542px" : undefined}
        sx={isDesktop ? undefined : { aspectRatio: "375 / 165" }}
        borderRadius={isDesktop ? "10px" : undefined}
        overflow="hidden"
      >
        <Image
          src={mapImage}
          alt=""
          position="absolute"
          objectFit="fill"
          // The desktop crop is wider than its box, which the app's global
          // `img { max-width: 100% }` would otherwise clamp away.
          maxWidth="none"
          {...MAP_CROP[variant]}
        />
        {/* Two overlays, stacked above the map and below the pins. */}
        <Box
          position="absolute"
          inset="0"
          background="rgba(255, 255, 255, 0.66)"
          pointerEvents="none"
        />
        <Box
          position="absolute"
          inset="0"
          backgroundImage={`linear-gradient(${fadeAngle(variant, side)}, transparent ${FADE_STOP[variant]}, ${sand100} 100%)`}
          pointerEvents="none"
        />
        {pins.map((pin, index) => {
          const position = POSITIONS[variant][index];

          if (!position) return null;

          return (
            <Box
              key={pin.face}
              position="absolute"
              left={`${position.left}%`}
              top={`${position.top}%`}
            >
              {/* Raised above the face, which it overlaps by its first 14px. */}
              <Box
                position="absolute"
                zIndex="1"
                left="36px"
                top="0"
                height="25px"
                borderRadius="full"
                background="white"
                boxShadow="0 0 10px rgba(0, 0, 0, 0.1)"
                paddingX="9px"
                fontSize="sm"
                lineHeight="25px"
                color="#333333"
                whiteSpace="nowrap"
                fontWeight="normal"
              >
                {pin.label}
              </Box>
              <Image
                src={pin.face}
                alt=""
                position="relative"
                width="50px"
                height="50px"
                borderRadius="full"
                objectFit="cover"
                border="2px solid white"
                boxShadow="0 0 10px rgba(0, 0, 0, 0.1)"
              />
            </Box>
          );
        })}
      </Box>
    );
  }
);

ServiceProviderMap.displayName = "ServiceProviderMap";
